'use client';

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MdClose } from 'react-icons/md';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button/Button';
import { ButtonSize, ButtonType, ButtonVariant } from '@/components/ui/button/Button.types';
import { Text } from '@/components/ui/text/Text';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import type { MessageModalProps } from './MessageModal.types';

export function MessageModal({
  open,
  onClose,
  icon,
  title,
  message,
  primaryAction,
  secondaryAction,
  showCloseButton = true,
  ariaLabel,
  'data-cy': dataCy = 'message-modal',
}: MessageModalProps) {
  const tCommon = useTranslations('Common');
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, open);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const hasActions = !!primaryAction || !!secondaryAction;

  const content = (
    <>
      <div
        className="tw:fixed tw:inset-0 tw:z-40 tw:bg-black/40 animate-backdrop-fade-in"
        aria-hidden
        onClick={onClose}
        data-cy="message-modal-backdrop"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? title}
        className="tw:fixed tw:bottom-0 tw:left-0 tw:right-0 tw:z-50 tw:flex tw:flex-col tw:rounded-t-[var(--radius-16)] tw:bg-white tw:shadow-lg animate-bottomsheet-slide-up tw:md:bottom-auto tw:md:left-1/2 tw:md:top-1/2 tw:md:w-[25rem] tw:md:-translate-x-1/2 tw:md:-translate-y-1/2 tw:md:rounded-[var(--radius-16)] md:animate-dialog"
        data-cy={dataCy}
      >
        {showCloseButton && (
          <div className="tw:flex tw:justify-end tw:p-small">
            <Button
              buttonType={ButtonType.text}
              size={ButtonSize.small}
              onClick={onClose}
              aria-label={tCommon('close')}
              data-cy="message-modal-close"
              className="tw:text-gray-dark"
              leftIcon={<MdClose size={24} />}
            />
          </div>
        )}

        <div
          className={`tw:flex tw:flex-col tw:items-center tw:gap-huge tw:px-huge ${
            showCloseButton ? 'tw:pb-huge' : 'tw:py-huge'
          }`}
        >
          {icon && (
            <div className="tw:flex tw:items-center tw:justify-center" aria-hidden="true">
              {icon}
            </div>
          )}

          <div className="tw:flex tw:flex-col tw:items-center tw:gap-small">
            <Text
              size="20"
              weight="bold"
              className="tw:text-font tw:text-center"
              data-cy="message-modal-title"
            >
              {title}
            </Text>
            <Text
              size="16"
              className="tw:text-gray-darker tw:text-center tw:leading-2"
              data-cy="message-modal-message"
            >
              {message}
            </Text>
          </div>

          {hasActions && (
            <div className="tw:flex tw:w-full tw:items-stretch tw:gap-small">
              {secondaryAction && (
                <Button
                  size={ButtonSize.large}
                  variant={secondaryAction.variant ?? ButtonVariant.secondary}
                  onClick={secondaryAction.onClick}
                  isLoading={secondaryAction.isLoading}
                  disabled={secondaryAction.disabled}
                  className="tw:flex-1"
                  data-cy={secondaryAction['data-cy'] ?? 'message-modal-secondary'}
                >
                  {secondaryAction.label}
                </Button>
              )}
              {primaryAction && (
                <Button
                  size={ButtonSize.large}
                  variant={primaryAction.variant ?? ButtonVariant.primary}
                  onClick={primaryAction.onClick}
                  isLoading={primaryAction.isLoading}
                  disabled={primaryAction.disabled}
                  className="tw:flex-1"
                  data-cy={primaryAction['data-cy'] ?? 'message-modal-primary'}
                >
                  {primaryAction.label}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(content, document.body);
}

MessageModal.displayName = 'MessageModal';
